import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, auth, storage } from '../../firebase';
import {
    collection, addDoc, getDocs, updateDoc, deleteDoc,
    doc, query, where, getDoc, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import Webcam from "react-webcam";

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Pagination, CircularProgress, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem, Autocomplete, Tooltip
} from '@mui/material';
import {
    Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon,
    Add as AddIcon, Close as CloseIcon, CameraAlt as CameraIcon,
    Autorenew as RetakeIcon, Visibility as ViewIcon
} from '@mui/icons-material';

const PurchaseOrders = () => {
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [viewPhoto, setViewPhoto] = useState(null);

    // Form State
    const [vendorId, setVendorId] = useState('');
    const [courierType, setCourierType] = useState('Direct');
    const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
    const [items, setItems] = useState([{ productId: '', qty: '', rate: '', total: 0 }]);

    // Webcam State
    const webcamRef = useRef(null);
    const [imgSrc, setImgSrc] = useState(null);
    const [showCamera, setShowCamera] = useState(false);

    // Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const sId = userDoc.data().schoolId;
                    setSchoolId(sId);
                    fetchInitialData(sId);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchInitialData = async (sId) => {
        const vSnap = await getDocs(query(collection(db, "vendors"), where("schoolId", "==", sId)));
        setVendors(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const pSnap = await getDocs(query(collection(db, "products"), where("schoolId", "==", sId)));
        setProducts(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        fetchOrders(sId);
    };

    const fetchOrders = async (sId) => {
        const q = query(collection(db, "purchaseOrders"), where("schoolId", "==", sId));
        const snap = await getDocs(q);
        setOrders(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        if (field === 'qty' || field === 'rate') {
            newItems[index].total = (parseFloat(newItems[index].qty) || 0) * (parseFloat(newItems[index].rate) || 0);
        }
        setItems(newItems);
    };

    const capture = useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot();
        setImgSrc(imageSrc);
        setShowCamera(false);
    }, [webcamRef]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const vendor = vendors.find(v => v.id === vendorId);

        try {
            let photoUrl = imgSrc || null;
            // Upload to Storage if it's a new base64 capture
            if (imgSrc && imgSrc.startsWith('data:image')) {
                const storageRef = ref(storage, `purchaseOrders/${schoolId}/${Date.now()}.jpg`);
                await uploadString(storageRef, imgSrc, 'data_url');
                photoUrl = await getDownloadURL(storageRef);
            }

            const payload = {
                vendorId,
                vendorName: vendor?.name || '',
                purchaseDate,
                courierType,
                items,
                grandTotal,
                photoUrl,
                schoolId,
                updatedAt: serverTimestamp()
            };

            if (editId) await updateDoc(doc(db, "purchaseOrders", editId), payload);
            else await addDoc(collection(db, "purchaseOrders"), { ...payload, createdAt: serverTimestamp() });

            handleClose();
            fetchOrders(schoolId);
        } catch (err) { console.error(err); }
        setSaving(false);
    };

    const handleClose = () => {
        setOpen(false);
        setEditId(null);
        setVendorId('');
        setCourierType('Direct');
        setItems([{ productId: '', qty: '', rate: '', total: 0 }]);
        setImgSrc(null);
        setShowCamera(false);
    };

    const startEdit = (o) => {
        setEditId(o.id);
        setVendorId(o.vendorId);
        setCourierType(o.courierType || 'Direct');
        setPurchaseDate(o.purchaseDate);
        setItems(o.items);
        setImgSrc(o.photoUrl);
        setOpen(true);
    };

    const addItemRow = () => setItems([...items, { productId: '', qty: '', rate: '', total: 0 }]);
    const removeItemRow = (index) => setItems(items.filter((_, i) => i !== index));
    const grandTotal = items.reduce((sum, item) => sum + (item.total || 0), 0);

    const filtered = orders.filter(o => (o.vendorName?.toLowerCase() || "").includes(searchTerm.toLowerCase()));
    const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    if (loading) return <Box sx={{ textAlign: 'center', mt: 10 }}><CircularProgress /></Box>;

    return (
        <Grid sx={{ mt: 1, justifyContent: 'center' }}>
            <Grid item xs={12} md={11}>
                <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                    <Grid className="row" alignItems="center" sx={{ mb: 1 }}>
                        <Grid className="col-sm-6" item xs={6}>
                            <Typography variant="h5" fontWeight="bold" color="primary">Purchase Orders</Typography>
                        </Grid>
                        <Grid className="col-sm-6" item xs={6} sx={{ textAlign: 'right' }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>New Order</Button>
                        </Grid>
                    </Grid>

                    <TextField
                        size="small"
                        placeholder="Search vendor..."
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 3, maxWidth: 400 }}
                        fullWidth
                        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'gray' }} /> }}
                    />

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell><b>Sr.No</b></TableCell>
                                    <TableCell><b>Date</b></TableCell>
                                    <TableCell><b>Vendor</b></TableCell>
                                    <TableCell><b>Type</b></TableCell>
                                    <TableCell><b>Amount</b></TableCell>
                                    <TableCell align="right"><b>Actions</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginated.map((o, i) => (
                                    <TableRow key={o.id} hover>
                                        <TableCell>{(page - 1) * itemsPerPage + i + 1}</TableCell>
                                        <TableCell>{o.purchaseDate}</TableCell>
                                        <TableCell>{o.vendorName}</TableCell>
                                        <TableCell>{o.courierType}</TableCell>
                                        <TableCell><b>₹{o.grandTotal}</b></TableCell>
                                        <TableCell align="right">
                                            {o.photoUrl && (
                                                <Tooltip title="View Proof">
                                                    <IconButton size="small" color="info" onClick={() => setViewPhoto(o.photoUrl)}><ViewIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                            )}
                                            <IconButton size="small" color="primary" onClick={() => startEdit(o)}><EditIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" onClick={async () => { if (window.confirm("Delete?")) { await deleteDoc(doc(db, "purchaseOrders", o.id)); fetchOrders(schoolId); } }}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination count={Math.ceil(filtered.length / itemsPerPage)} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} onChange={(e, v) => setPage(v)} />
                </Paper>
            </Grid>

            {/* Entry Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ fontWeight: 'bold' }}>{editId ? 'Edit Order' : 'Create New Purchase Order'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid className="row" spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} className="col-sm-4">
                                <TextField select fullWidth label="Vendor" size="small" value={vendorId} onChange={(e) => setVendorId(e.target.value)} required>
                                    {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} className="col-sm-4">
                                <TextField select fullWidth label="Courier/Delivery" size="small" value={courierType} onChange={(e) => setCourierType(e.target.value)} required>
                                    <MenuItem value="Direct">Direct Delivery</MenuItem>
                                    <MenuItem value="Courier">Courier</MenuItem>
                                    <MenuItem value="Self Pick">Self Pick</MenuItem>
                                    <MenuItem value="Transport">Transport</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} className="col-sm-4">
                                <TextField fullWidth label="Date" type="date" size="small" required value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ mb: 2 }}><Typography variant="caption" fontWeight="bold">ITEM LIST</Typography></Divider>

                        {items.map((item, index) => (
                            <Grid className="row" spacing={1} key={index} alignItems="center" sx={{ mb: 1.5 }}>
                                <Grid className="col-sm-1 mb-2" item xs={0.5}><Typography variant="body2">{index + 1}</Typography></Grid>
                                <Grid className="col-sm-4    mb-2" item xs={11} sm={4}>
                                    <TextField select fullWidth label="Product" size="small" value={item.productId} onChange={(e) => handleItemChange(index, 'productId', e.target.value)} required>
                                        {products.map(p => <MenuItem key={p.id} value={p.id}>{p.productName}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid className="col-sm-2 mb-2" item xs={3} sm={2}><TextField fullWidth label="Qty" size="small" type="number" value={item.qty} onChange={(e) => handleItemChange(index, 'qty', e.target.value)} required /></Grid>
                                <Grid className="col-sm-2 mb-2" item xs={4} sm={2}><TextField fullWidth label="Rate" size="small" type="number" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} required /></Grid>
                                <Grid className="col-sm-2 mb-2" item xs={4} sm={2.5}><TextField fullWidth label="Total" size="small" value={item.total} disabled /></Grid>
                                <Grid className="col-sm-1 mb-2" item xs={1}>
                                    <IconButton size="small" color="error" onClick={() => removeItemRow(index)} disabled={items.length === 1}><CloseIcon fontSize="small" /></IconButton>
                                </Grid>
                            </Grid>
                        ))}

                        <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={addItemRow} sx={{ mt: 1 }}>Add Row</Button>

                        <Paper variant="outlined" sx={{ mt: 3, p: 2, textAlign: 'right', bgcolor: '#f9f9f9' }}>
                            <Typography variant="h6">Grand Total: <b>₹{grandTotal}</b></Typography>
                        </Paper>

                        {/* Webcam Section */}
                        <Box sx={{ mt: 3, p: 2, border: '1px dashed #aaa', textAlign: 'center', borderRadius: 2 }}>
                            {showCamera ? (
                                <Box>
                                    <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" width="100%" style={{ maxWidth: '400px', borderRadius: '8px' }} />
                                    <Box sx={{ mt: 1 }}>
                                        <Button variant="contained" onClick={capture}>Capture Proof</Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    {imgSrc ? (
                                        <Box>
                                            <img src={imgSrc} alt="Proof" style={{ width: '150px', borderRadius: '8px' }} />
                                            <Box sx={{ mt: 1 }}>
                                                <Button size="small" startIcon={<RetakeIcon />} onClick={() => setShowCamera(true)}>Retake Photo</Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Button variant="outlined" startIcon={<CameraIcon />} onClick={() => setShowCamera(true)}>Add Bill/Parcel Photo</Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save Order'}</Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Photo Viewer */}
            <Dialog open={!!viewPhoto} onClose={() => setViewPhoto(null)}>
                <DialogTitle>Order Proof</DialogTitle>
                <DialogContent>
                    <img src={viewPhoto} alt="Order Proof" style={{ width: '100%', borderRadius: '8px' }} />
                </DialogContent>
                <DialogActions><Button onClick={() => setViewPhoto(null)}>Close</Button></DialogActions>
            </Dialog>
        </Grid>
    );
};

export default PurchaseOrders;